/* eslint-disable */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../supabase';
import { isSuperAdmin, setSuperAdminCache } from '../utils/superAdmin';

export function usePortalPermissions() {
  return useQuery({
    queryKey: ['portal-permissions'],
    queryFn: async () => {
      // AUD-05 P1: the signed-in user from the local session, not a network round trip per run.
      // The id only scopes two own-row reads that RLS checks against the JWT anyway.
      const { data: { session } } = await supabase.auth.getSession();
      const user = session?.user || null;
      if (!user) return { permissions: [], user: null, isSuperAdmin: false };

      // Fetch permissions AND super admin flag in parallel
      const [permsRes, profileRes] = await Promise.all([
        supabase.from('user_portal_permissions').select('portal_id, role').eq('user_id', user.id),
        supabase.from('user_profiles').select('is_super_admin, mi_access').eq('id', user.id).maybeSingle(),
      ]);

      const isSuper = !!profileRes.data?.is_super_admin;
      // Populate the sync cache for isSuperAdmin() calls elsewhere
      setSuperAdminCache(user.id, isSuper);

      if (permsRes.error) throw permsRes.error;
      // miAccess = the grant-only Mail Intelligence flag (own-row read; RLS lets a user see their own).
      return { permissions: permsRes.data || [], user, isSuperAdmin: isSuper, miAccess: !!profileRes.data?.mi_access };
    },
    // Access-engine Step 1 — reactive sync. Realtime (useUserPermissionsRealtime,
    // channel 'user-perms-sync') invalidates this on a grant/revoke for instant update;
    // refetchOnWindowFocus is the fallback for a missed event and for the two profile
    // flags the channel does not watch. AUD-05 P1: staleTime 60 s → 5 min — ~20 mounts
    // re-ran all three reads on every screen change once a minute had passed.
    staleTime: 5 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    refetchOnWindowFocus: true,
  });
}

// The access rule as a PURE function over an already-fetched permissions payload.
// Extracted (P5a) so a caller that must answer for MANY portals at once can apply
// the identical rule without calling a hook in a loop — the notification
// preferences panel lists one row per type and each type names its own portal.
// useCanAccessPortal below delegates here, so there is ONE rule, not two that
// drift (Convention #61). `role !== 'none'` mirrors Convention #172: a row is not
// access, and 'none' is an explicit denial.
//
// Fail-closed is the CALLER's job here (this function has no loading signal):
// pass a payload only once usePortalPermissions has resolved.
export function canAccessPortal(data, portalId) {
  if (data?.isSuperAdmin) return true;
  if (data?.user && isSuperAdmin(data.user)) return true;
  const perm = (data?.permissions || []).find(p => p.portal_id === portalId);
  if (!perm) return false;
  return perm.role !== 'none';
}

export function useCanAccessPortal(portalId) {
  const { data, isLoading } = usePortalPermissions();

  // Use the fetched isSuperAdmin flag (not the sync cache which may be stale)
  if (data?.isSuperAdmin) return true;

  // Also check sync cache as fallback (populated by useAuthSetup)
  if (data?.user && isSuperAdmin(data.user)) return true;

  // Fail-closed while loading
  if (isLoading) return false;

  return canAccessPortal(data, portalId);
}

export function usePortalRole(portalId) {
  const { data } = usePortalPermissions();
  const permissions = data?.permissions || [];
  const perm = permissions.find(p => p.portal_id === portalId);
  return perm?.role || null;
}

// Single source of truth for Mail Intelligence visibility (Slice 1.3 — grant-only). Mirrors the DB
// gate _mi_is_admin(): (admin OR super-admin) AND the explicit mi_access grant. Deliberately does NOT
// reuse useCanAccessPortal (which auto-passes every super-admin) — a non-granted super-admin sees
// nothing. Fail-closed while loading, so MI never flashes for a non-granted user. `role` is the
// caller's global role (AppHeader/DevelopmentsTab already have it).
export function useCanSeeMailIntel(role) {
  const { data } = usePortalPermissions();
  if (!data) return false;
  return isMiAdmin(data, role);
}

// Shared MI predicates (mirror the DB gates) — so no site hand-rolls them.
// isMiAdmin = MI VISIBILITY, mirrors _mi_is_admin(): (admin OR super-admin) AND mi_access.
export function isMiAdmin(data, role) {
  return (role === 'admin' || !!data?.isSuperAdmin) && !!data?.miAccess;
}
// isMiManager = MI MANAGEMENT (grant/revoke) — STRICTER: super-admin AND mi_access
// (a plain admin cannot manage MI grants). Kept distinct from isMiAdmin on purpose.
export function isMiManager(data) {
  return !!data?.isSuperAdmin && !!data?.miAccess;
}

export function usePermissionsReady() {
  const { isLoading, isError } = usePortalPermissions();
  return !isLoading && !isError;
}
